import { clamp, lerp, smooth } from '../../../motion'
import { build, rng } from './formations'

/**
 * Partikelfeld aus dem Aivy-Formenvokabular (Kreis, Quadrat und Dreieck mit
 * runden Ecken, Halbkreis, Viertelkreis, Balken, Quadrat als Kontur) auf einer
 * festen Leinwand hinter den Szenen. In lockeren Formationen wachsen die Formen,
 * einige werden zu großen Akzenten wie in den Aivy-Illustrationen. Die Formationen hängen an Keyframes auf der
 * Scrollachse (`keys`, sortiert nach y). Zwischen zwei Keyframes morphen die
 * Partikel versetzt und auf leicht gebogenen Bahnen. Der Mauszeiger schiebt
 * sie sanft zur Seite.
 */

// Index 0 bis 5 je Partikel, danach Grau (Chaos, Aussortierte) und Weiß (tint: 'white')
const DARK = ['#407BFF', '#43DBBD', '#FFB628', '#FE79A7', '#FFFFFF', '#8FB0FF']
const LIGHT = ['#407BFF', '#1FB89E', '#F2A20C', '#F25C93', '#182033', '#2D45A0']
const GREY = { dark: '#4A5574', light: '#B4BCCD' }
const WHITE = '#FFFFFF'
const WEIGHTS = [0.3, 0.2, 0.15, 0.15, 0.1, 0.1]
const LEVELS = [0.15, 0.32, 0.55, 0.8, 1]
const REPEL = 110

// Formen: 0 Kreis, 1 Quadrat, 2 Dreieck, 3 Halbkreis, 4 Viertelkreis, 5 Balken, 6 Kontur-Quadrat
const KINDS = [0.15, 0.21, 0.21, 0.15, 0.11, 0.09, 0.08]
// Anteil der großen Akzent-Formen
const BIG = 0.05

const pick = (r, weights) => {
  let x = r()
  for (let i = 0; i < weights.length; i++) {
    x -= weights[i]
    if (x <= 0) return i
  }
  return 0
}

// Vieleck mit runden Ecken, `rev` dreht die Laufrichtung (Loch bei nonzero)
const RX = new Float32Array(4)
const RY = new Float32Array(4)
function roundPoly(path, x, y, rad, corners, ang, round, rev) {
  for (let k = 0; k < corners; k++) {
    const ca = ang + ((rev ? -k : k) * Math.PI * 2) / corners
    RX[k] = x + Math.cos(ca) * rad
    RY[k] = y + Math.sin(ca) * rad
  }
  for (let k = 0; k < corners; k++) {
    const p = (k + corners - 1) % corners
    const q = (k + 1) % corners
    const ax = RX[k] + (RX[p] - RX[k]) * round
    const ay = RY[k] + (RY[p] - RY[k]) * round
    if (k === 0) path.moveTo(ax, ay)
    else path.lineTo(ax, ay)
    path.quadraticCurveTo(
      RX[k],
      RY[k],
      RX[k] + (RX[q] - RX[k]) * round,
      RY[k] + (RY[q] - RY[k]) * round
    )
  }
  path.closePath()
}

function shape(path, kind, x, y, s, ang) {
  switch (kind) {
    case 0:
      path.moveTo(x + s * 0.55, y)
      path.arc(x, y, s * 0.55, 0, Math.PI * 2)
      break
    case 1:
      roundPoly(path, x, y, s * 0.74, 4, ang, 0.2)
      break
    case 2:
      roundPoly(path, x, y, s * 0.78, 3, ang, 0.16)
      break
    case 3: {
      // Mittelpunkt der Grundlinie leicht versetzt, damit die Fläche mittig sitzt
      const r = s * 0.72
      const cx = x - Math.cos(ang + Math.PI / 2) * r * 0.42
      const cy = y - Math.sin(ang + Math.PI / 2) * r * 0.42
      path.moveTo(cx + Math.cos(ang) * r, cy + Math.sin(ang) * r)
      path.arc(cx, cy, r, ang, ang + Math.PI)
      path.closePath()
      break
    }
    case 4: {
      const r = s * 1.02
      const cx = x - Math.cos(ang + Math.PI / 4) * r * 0.42
      const cy = y - Math.sin(ang + Math.PI / 4) * r * 0.42
      path.moveTo(cx, cy)
      path.arc(cx, cy, r, ang, ang + Math.PI / 2)
      path.closePath()
      break
    }
    case 5: {
      // Balken mit runden Enden
      const l = s * 1.25
      const r = s * 0.24
      const dx = Math.cos(ang)
      const dy = Math.sin(ang)
      path.moveTo(x + dx * l - dy * r, y + dy * l + dx * r)
      path.arc(
        x + dx * l,
        y + dy * l,
        r,
        ang + Math.PI / 2,
        ang - Math.PI / 2,
        true
      )
      path.lineTo(x - dx * l + dy * r, y - dy * l - dx * r)
      path.arc(
        x - dx * l,
        y - dy * l,
        r,
        ang - Math.PI / 2,
        ang + Math.PI / 2,
        true
      )
      path.closePath()
      break
    }
    default:
      roundPoly(path, x, y, s * 0.95, 4, ang, 0.22)
      roundPoly(path, x, y, s * 0.62, 4, ang, 0.22, true)
  }
}

export class Field {
  constructor(canvas, { theme = 'dark' } = {}) {
    const light = theme === 'light'
    this.colors = [...(light ? LIGHT : DARK), GREY[theme] || GREY.dark, WHITE]
    this.canvas = canvas
    this.ctx = canvas.getContext('2d')
    this.cache = new Map()
    this.mouse = { x: -9999, y: -9999, tx: -9999, ty: -9999 }
    this.n = 0
    this.resize()
  }

  resize() {
    const w = window.innerWidth
    const h = window.innerHeight
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    this.canvas.width = Math.round(w * dpr)
    this.canvas.height = Math.round(h * dpr)
    this.canvas.style.width = `${w}px`
    this.canvas.style.height = `${h}px`
    this.w = w
    this.h = h
    this.dpr = dpr
    this.narrow = w < 720
    const n = w < 720 ? 700 : w < 1200 ? 1100 : 1400
    if (n !== this.n) this.seed(n)
    this.cache.clear()
  }

  seed(n) {
    const r = rng(42)
    this.n = n
    this.kind = new Uint8Array(n)
    this.color = new Uint8Array(n)
    this.size = new Float32Array(n)
    this.big = new Float32Array(n)
    this.delay = new Float32Array(n)
    this.swirl = new Float32Array(n)
    this.phase = new Float32Array(n)
    this.spin = new Float32Array(n)
    this.ox = new Float32Array(n)
    this.oy = new Float32Array(n)
    for (let i = 0; i < n; i++) {
      this.kind[i] = pick(r, KINDS)
      this.color[i] = pick(r, WEIGHTS)
      this.size[i] = 3 + r() * 3.4
      this.big[i] = r() < BIG ? 2.2 + r() * 1.6 : 0
      this.delay[i] = r() * 0.35
      this.swirl[i] = (r() - 0.5) * 2
      this.phase[i] = r() * Math.PI * 2
      // Aivy-Formen stehen schräg und drehen sich nur langsam
      this.spin[i] = (r() - 0.5) * 0.5
    }
  }

  point(x, y) {
    this.mouse.tx = x
    this.mouse.ty = y
  }

  formation(key) {
    const id = `${key.id}@${this.w}x${this.h}`
    let f = this.cache.get(id)
    if (!f) {
      f = build(key.spec(this.w, this.h, this.narrow), this.w, this.h, this.n)
      this.cache.set(id, f)
    }
    return f
  }

  render({ y, now, keys, ls, reduce }) {
    const { ctx, n, w, h } = this
    const t = now / 1000
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
    ctx.clearRect(0, 0, w, h)
    if (!keys.length) return

    let a = 0
    while (a < keys.length - 1 && keys[a + 1].y <= y) a++
    const b = Math.min(keys.length - 1, a + 1)
    const span = keys[b].y - keys[a].y
    const raw = span > 0 ? clamp((y - keys[a].y) / span) : 0
    const m = y < keys[0].y ? 0 : smooth(clamp((raw - 0.1) / 0.75))

    const A = this.formation(keys[a])
    const B = this.formation(keys[b])
    if (A.dyn) A.dyn(t, ls[keys[a].sec] ?? 0)
    if (B !== A && B.dyn) B.dyn(t, ls[keys[b].sec] ?? 0)
    const opacity = lerp(A.opacity, B.opacity, m)
    if (opacity < 0.01) return

    const mouse = this.mouse
    mouse.x += (mouse.tx - mouse.x) * 0.2
    mouse.y += (mouse.ty - mouse.y) * 0.2

    const paths = new Array(this.colors.length * LEVELS.length)
    for (let i = 0; i < n; i++) {
      const mi = smooth(clamp(m * 1.35 - this.delay[i]))
      const ax = A.pos[i * 2]
      const ay = A.pos[i * 2 + 1]
      const bx = B.pos[i * 2]
      const by = B.pos[i * 2 + 1]
      let x = ax + (bx - ax) * mi
      let py = ay + (by - ay) * mi

      // Gebogene Bahn beim Morphen
      if (mi > 0 && mi < 1) {
        const dx = bx - ax
        const dy = by - ay
        const len = Math.hypot(dx, dy) || 1
        const arc =
          Math.sin(mi * Math.PI) * this.swirl[i] * Math.min(len * 0.3, 160)
        x += (-dy / len) * arc
        py += (dx / len) * arc
      }
      if (!reduce) {
        x += Math.sin(t * 0.5 + this.phase[i]) * 2.4
        py += Math.cos(t * 0.43 + this.phase[i] * 1.7) * 2.4
      }

      // Maus schiebt Partikel weg, sie federn zurück
      const mx = x - mouse.x
      const my = py - mouse.y
      const d2 = mx * mx + my * my
      let tx = 0
      let ty = 0
      if (d2 < REPEL * REPEL) {
        const d = Math.sqrt(d2) || 1
        const f = (1 - d / REPEL) * 46
        tx = (mx / d) * f
        ty = (my / d) * f
      }
      this.ox[i] += (tx - this.ox[i]) * 0.14
      this.oy[i] += (ty - this.oy[i]) * 0.14
      x += this.ox[i]
      py += this.oy[i]

      if (x < -20 || x > w + 20 || py < -20 || py > h + 20) continue
      const alpha = lerp(A.alpha[i], B.alpha[i], mi) * opacity
      if (alpha < 0.04) continue
      const late = mi >= 0.5
      const grey = late ? B.grey?.[i] : A.grey?.[i]
      const tint = late ? B.tint : A.tint
      const c = grey ? 6 : tint === 'white' ? 7 : this.color[i]
      let lv = 0
      while (lv < LEVELS.length - 1 && LEVELS[lv] < alpha - 0.08) lv++
      const slot = c * LEVELS.length + lv
      const path = paths[slot] || (paths[slot] = new Path2D())

      // Lockere Formationen vergrößern alle Formen, Akzente wachsen stärker
      const bloom = lerp(A.bloom, B.bloom, mi)
      const s =
        this.size[i] *
        lerp(A.size, B.size, mi) *
        (1 + bloom * (0.3 + this.big[i]))
      const ang = this.phase[i] + (reduce ? 0 : t * this.spin[i])
      shape(path, this.kind[i], x, py, s, ang)
    }

    for (let slot = 0; slot < paths.length; slot++) {
      const path = paths[slot]
      if (!path) continue
      const c = Math.floor(slot / LEVELS.length)
      ctx.globalAlpha = LEVELS[slot % LEVELS.length]
      ctx.fillStyle = this.colors[c]
      ctx.fill(path)
    }
    ctx.globalAlpha = 1
  }
}
